import {
  Calendar,
  ChevronDown,
  ExternalLink,
  File,
  FileText,
  Folder,
  HardDrive,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleDriveListFiles } from 'sprites-types/workflow/tools/googleDriveListFiles'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function DriveFileItem({ file, className }: { file: any; className?: string }) {
  const name = file.name || file.title || 'Unknown file'
  const mimeType = file.mimeType || file.type || ''
  const size = file.size || file.fileSize || 0
  const modifiedTime = file.modifiedTime || file.lastModified || file.dateModified
  const webViewLink = file.webViewLink || file.viewLink || file.url
  const webContentLink = file.webContentLink || file.downloadLink
  const shared = file.shared || file.isShared || false

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return ''
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return dateStr
    }
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('folder')) return <Folder className="h-4 w-4 text-blue-500" />
    if (mimeType.includes('document') || mimeType.includes('text'))
      return <FileText className="h-4 w-4 text-blue-600" />
    return <File className="h-4 w-4 text-gray-500" />
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">{getFileIcon(mimeType)}</div>

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <div className="truncate text-sm font-medium text-gray-900">{name}</div>
              {shared && (
                <div className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-600">
                  Shared
                </div>
              )}
            </div>

            <div className="mb-2 flex items-center gap-3 text-xs text-gray-600">
              {size > 0 && <span>{formatFileSize(parseInt(size.toString()))}</span>}
              {mimeType && size > 0 && <span>•</span>}
              {mimeType && <span>{mimeType.split('/').pop() || mimeType}</span>}
              {modifiedTime && (mimeType || size > 0) && <span>•</span>}
              {modifiedTime && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{formatDate(modifiedTime)}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {webViewLink && (
                <a
                  href={webViewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  View
                </a>
              )}
              {webContentLink && (
                <a
                  href={webContentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  Download
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function GoogleDriveListFilesDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleDriveListFiles, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const files = Array.isArray(result)
    ? result
    : (result as any)?.files || (result as any)?.data || (result ? [result] : [])
  const totalSize = files.reduce((sum: number, f: any) => {
    const size = f.size || f.fileSize || 0
    return sum + (typeof size === 'string' ? parseInt(size) : size)
  }, 0)

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (files.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <HardDrive className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No Google Drive files found</div>
        </div>
      </div>
    )
  }

  if (files.length === 1) {
    return <DriveFileItem file={files[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <HardDrive className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Google Drive Files</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {files.length} files • {formatFileSize(totalSize)}
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {files.map((file: any, index: number) => (
            <DriveFileItem key={file.id || file.name || index} file={file} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadGoogleDriveListFiles(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleDriveListFilesToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleDriveListFiles, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const files = Array.isArray(result)
    ? result
    : (result as any)?.files || (result as any)?.data || (result ? [result] : [])

  if (files.length === 0) {
    return `## Google Drive Files

No files found.

---
`
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return ''
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const filesMarkdown = files
    .map((file: any) => {
      const name = file.name || file.title || 'Unknown file'
      const id = file.id || file.fileId
      const mimeType = file.mimeType || file.type || ''
      const size = file.size || file.fileSize || 0
      const modifiedTime = file.modifiedTime || file.lastModified || file.dateModified
      const webViewLink = file.webViewLink || file.viewLink || file.url

      return `### ${name}

${id ? `**ID:** ${id}` : ''}
${mimeType ? `**Type:** ${mimeType}` : ''}
${size > 0 ? `**Size:** ${formatFileSize(typeof size === 'string' ? parseInt(size) : size)}` : ''}
${modifiedTime ? `**Modified:** ${new Date(modifiedTime).toLocaleString()}` : ''}
${webViewLink ? `**Link:** ${webViewLink}` : ''}
`
    })
    .join('\n---\n\n')

  return `## Google Drive Files (${files.length} files)

${filesMarkdown}

---
`
}

export const GoogleDriveListFilesDisplay = createWorkflowToolComponent(
  GoogleDriveListFilesDisplayComponent,
  downloadGoogleDriveListFiles,
  googleDriveListFilesToMarkdown
)
